// Libs
import { Fragment, ReactElement, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CloseRounded } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, CircularProgress, Fab } from '@mui/material';
import Grid from '@mui/material/Grid';
import { FieldArray, useFormikContext } from 'formik';

// Components
import { Button, FormikDropdown, FormikInputField } from 'components';
import { ITypeaheadOption } from 'components/Typeahead';
import { WizardButtonRow } from '../WizardButtonRow';

// Styles
import styles from '../Wizard.module.scss';

// Core
import { IProfileStateBoard, IStateOrProvince, IUserProfile, useAppDispatch } from 'core';

// Store
import { stateAndProvinceSelector } from 'store/statesAndProvinces/selectors';
import { getStatesAndProvinces } from 'store/statesAndProvinces/actions';

// Types
import { IWizardProps } from '../types';

type IProps = Pick<IWizardProps, 'back' | 'next' | 'skip'>;

export const StateBoards = ({ back, skip, next }: IProps): ReactElement => {
  const dispatch = useAppDispatch();
  const { values } = useFormikContext<IUserProfile>();
  const statesAndProvinces: IStateOrProvince[] = useSelector(stateAndProvinceSelector);
  const stateOptions: ITypeaheadOption[] = useMemo(
    () =>
      statesAndProvinces
        ?.filter(({ stateName }: IStateOrProvince): boolean => !!stateName)
        ?.map(
          ({ stateName, isoStateCode }: IStateOrProvince): ITypeaheadOption => ({
            label: stateName,
            value: isoStateCode,
          }),
        ),
    [statesAndProvinces],
  );

  useEffect((): void => {
    dispatch(getStatesAndProvinces());
  }, [dispatch]);

  const { stateBoards } = values;

  // Get all of the ids that have been selected so that we can disable them from the picker.
  const filteredStateIds = (stateBoards ?? [])
    .map(({ stateAbbreviation }: IProfileStateBoard): string => stateAbbreviation)
    .filter(Boolean);

  return (
    <>
      <h5>In which state(s) are you licensed?</h5>
      <br />
      <div className={styles['boards-repeater']}>
        {!statesAndProvinces?.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <>
            <FieldArray name="stateBoards">
              {({ push, remove }): ReactElement => {
                return (
                  <>
                    <Grid container spacing={3}>
                      {stateBoards?.map((board: IProfileStateBoard, index: number): ReactElement => {
                        const { isEditable, stateAbbreviation } = board;

                        return (
                          <Fragment key={`${index}-${stateAbbreviation}`}>
                            <Grid size={{ xs: 6, sm: 7 }}>
                              <FormikDropdown
                                disabled={isEditable === false}
                                disabledItemKeys={filteredStateIds}
                                formikKey={`stateBoards.${index}.stateAbbreviation`}
                                idProp="value"
                                items={stateOptions}
                                labelProp="label"
                              />
                            </Grid>
                            <Grid size={{ xs: 5, sm: 4 }}>
                              <FormikInputField
                                disabled={isEditable === false}
                                formikKey={`stateBoards.${index}.learnerId`}
                                placeholder="ID:"
                              />
                            </Grid>
                            {isEditable !== false && (
                              <Grid className={styles.remove} size={{ xs: 1 }}>
                                <Button className="remove" onClick={() => remove(index)}>
                                  <CloseRounded sx={{ color: 'black' }} />
                                </Button>
                              </Grid>
                            )}
                          </Fragment>
                        );
                      })}
                    </Grid>
                    <div className={styles.add}>
                      <Fab
                        aria-label="add another instance"
                        color="inherit"
                        size="small"
                        onClick={() => push({ isEditable: true })}
                      >
                        <AddIcon color="inherit" />
                      </Fab>
                      <span className={styles.text}>Add Another Licensing State</span>
                    </div>
                  </>
                );
              }}
            </FieldArray>
            <div className={styles.caption}>
              ACCME has a direct reporting relationship with some{' '}
              <a href="https://accme.org/state-medical-boards-collaboration" target="_blank" rel="noopener noreferrer">
                licensing boards
              </a>
              . CME providers can report your participation on your behalf to those boards.
            </div>
          </>
        )}
        <WizardButtonRow skip={skip} next={next} back={back} />
      </div>
    </>
  );
};
